// Dependencies
import React, { useCallback } from 'react';
import isEqual from 'lodash.isequal';
import { useDispatch, useSelector } from 'react-redux';

// Types
import { ModalStateType, modalIds, UNDISMISSABLE_MODAL_IDS } from './types';

// Selectors
import { getCurrentModal } from '../../../state/selectors/ui/modal';

// Actions
import { closeModal } from '../../../state/actions/ui/modal';

// Container
import BaseModal from '../containers/BaseModal';
import { isModalCloseLocked } from '../modalCloseLock';
import { ModalErrorBoundary } from '../ModalErrorBoundary';

// Components
import { DownloadHistoricModal } from '../submodals/DownloadHistoricModal';
import { DownloadMultimediaPhotosModal } from '../submodals/DownloadMultimediaPhotosModal';
import { RelatesCommunicationModal } from '../submodals/RelatesCommunicationModal';
import { ChangeCampaignToOT } from '../submodals/ChangeCampaignToOT';
import { deselectedRowModalTable } from '../../../state/actions/ui/selectRowTableModal';
import { AddMediaModal } from '../submodals/AddMediaModal';
import { DeletePhotoModal } from '../submodals/DeletePhotoModal';
import { OpenCameraModal } from '../submodals/OpenCameraModal';
import { VersionUpdateModal } from '../submodals/VersionUpdateModal';
import { ChangePasswordModal } from '../submodals/ChangePasswordModal';

// Types
import { ModalProps as ModalPropsDownloadHistorical } from '../submodals/DownloadHistoricModal/types';
import { ModalProps as ModalPropsDownloadMultimediaPhotos } from '../submodals/DownloadMultimediaPhotosModal/types';
import { Img } from '../../forms/AddBasicInfoAccidentForm/types';
import { AddSketchModal } from '../submodals/AddSketchModal';
import { AddActionSketchModal } from '../submodals/AddActionSketchModal';
import { CustomFile } from '../../forms/ActionForms/ActionEdition/types';
import { ImageData } from '../../inputs/InputLoadImages/types';
import { ViewFullscreenImage } from '../submodals/ViewFulscreenImage';
import { ChangeStateModal } from '../submodals/ChangeStateModal';
import { TransferSubOrderModal } from '../submodals/TransferSubOrderModal';
import { ConfirmTransferModal } from '../submodals/ConfirmTransferModal';
import { EditTeamModal } from '../submodals/EditTeamModal';
import { DeleteTeamModal } from '../submodals/DeleteTeamModal';
import { DeleteUserGroupModal } from '../submodals/DeleteUserGroupModal';
import { EditUserGroupModal } from '../submodals/EditUserGroupModal';
import { EditUserGroupModalData } from '../submodals/EditUserGroupModal/types';
import { EditExternalCompanyModal } from '../submodals/EditExternalCompanyModal';
import { DeleteExternalCompanyModal } from '../submodals/DeleteExternalCompanyModal';
import { RoutePresetModal } from '../submodals/RoutePresetModal';
import { DeleteRoutePresetModal } from '../submodals/DeleteRoutePresetModal';
import { ValidationErrorsModal } from '../submodals/ValidationErrorsModal';
import { ConfirmCloseOrderModal } from '../submodals/ConfirmCloseOrderModal';
import { PauseSurveillanceRouteModal } from '../submodals/PauseSurveillanceRouteModal';
import { ContinueShiftModal } from '../submodals/ContinueShiftModal';
import { ContinueShiftModalData } from '../submodals/ContinueShiftModal/types';
import { NewSurveillanceSegmentModal } from '../submodals/NewSurveillanceSegmentModal';
import { NewWidgetModal } from '../../../pages/main/Dashboard/components/NewWidgetModal';
import { GpsRequiredModal } from '../submodals/GpsRequiredModal';
import { ChangeDirectionModal } from '../submodals/ChangeDirectionModal';
import { RelateSegmentModal } from '../submodals/RelateSegmentModal';
import { RelateOrderToRouteModal } from '../submodals/RelateOrderToRouteModal';
import { ChangePauseCauseModal } from '../submodals/ChangePauseCauseModal';
import { ForceCloseRouteModal } from '../submodals/ForceCloseRouteModal';
import { EditSegmentModal } from '../submodals/EditSegmentModal';
import { RegisterStopModal } from '../submodals/RegisterStopModal';
import {
  UndoSegmentStopModal,
  UndoSegmentStopModalData,
} from '../submodals/UndoSegmentStopModal';
import {
  UnreviewAllSegmentsModal,
  UnreviewAllSegmentsModalData,
} from '../submodals/UnreviewAllSegmentsModal';
import type { ValidateReviewWarning } from '../../../utils/surveillanceRoute';
import { BackgroundLocationRequiredModal } from '../submodals/BackgroundLocationRequiredModal';
import { BackgroundLocationRequiredModalData } from '../submodals/BackgroundLocationRequiredModal/types';
import { PhotoPermissionsRequiredModal } from '../submodals/PhotoPermissionsRequiredModal';
import { PhotoPermissionsRequiredModalData } from '../submodals/PhotoPermissionsRequiredModal/types';

// V4 PR 4+5 modal types
import { GpsRequiredModalData } from '../submodals/GpsRequiredModal/types';
import { VersionUpdateMode } from '../submodals/VersionUpdateModal/types';
import { ChangeDirectionModalData } from '../submodals/ChangeDirectionModal/types';
import { RelateSegmentModalData } from '../submodals/RelateSegmentModal/types';
import { RelateOrderToRouteModalData } from '../submodals/RelateOrderToRouteModal/types';
import { ChangePauseCauseModalData } from '../submodals/ChangePauseCauseModal/types';
import { ForceCloseRouteModalData } from '../submodals/ForceCloseRouteModal/types';
import { EditSegmentModalData } from '../submodals/EditSegmentModal/types';
import { RegisterStopModalData } from '../submodals/RegisterStopModal/types';
import { TransferSegmentModal } from '../submodals/TransferSegmentModal';
import { TransferSegmentModalData } from '../submodals/TransferSegmentModal/types';
import { MoveSegmentsModal } from '../submodals/MoveSegmentsModal';
import { MoveSegmentsModalData } from '../submodals/MoveSegmentsModal/types';

// Surveillance Route Modal Types
import { PauseSurveillanceRouteModalData } from '../submodals/PauseSurveillanceRouteModal/types';
import { NewSurveillanceSegmentModalData } from '../submodals/NewSurveillanceSegmentModal/types';

// Dashboard Widget Types
import { DashboardWidgetCatalogEntry } from '../../../pages/main/Dashboard/registry';

// EditTeamModal Types
import { EditTeamModalData } from '../submodals/EditTeamModal/types';
import { EditExternalCompanyModalData } from '../submodals/EditExternalCompanyModal/types';
import { RoutePresetModalData } from '../submodals/RoutePresetModal/types';
// EditMaterialTeamsModal (materiales por equipo, 2026-07-31)
import { EditMaterialTeamsModal } from '../submodals/EditMaterialTeamsModal';
import { EditMaterialTeamsModalData } from '../submodals/EditMaterialTeamsModal/types';

// ValidationErrorsModal Types
import { TabValidationStatus } from '../../../utils/formValidation';

export default function SuperModal() {
  // Dispatch
  const dispatch = useDispatch();

  // Selectors
  const currentModal: ModalStateType = useSelector(getCurrentModal, isEqual);

  /**
   * @function handleClickClose():
   * @description Close the modal on click close.
   */
  const handleClickClose = useCallback(() => {
    // QA 15-oct (T1.11+a): un modal que está guardando no se cierra — ni con
    // un clic fuera, ni con Escape, ni con la X. Si fallara, lo escrito se
    // perdería con él. Lo suelta el propio modal al acabar.
    if (isModalCloseLocked()) return;

    // V4 P14 (2026-04-22): GPS_REQUIRED es bloqueante — click-away y Escape
    // no deben cerrarlo. Se cierra únicamente cuando el consumidor llama
    // `closeModal()` explícitamente tras detectar GPS válido (auto-dismiss).
    //
    // BACKGROUND_LOCATION_REQUIRED va en el mismo saco: lo cierra
    // `useBackgroundLocationGate` cuando detecta el permiso concedido al
    // volver de Ajustes. Y PHOTO_PERMISSIONS_REQUIRED igual, con
    // `usePhotoPermissionsGate` (spec fotos geolocalizadas, § 14 y § 15). La lista vive
    // en `UNDISMISSABLE_MODAL_IDS`.
    if (currentModal?.id && UNDISMISSABLE_MODAL_IDS.has(currentModal.id)) {
      return;
    }

    // Actualización marcada como obligatoria en el manifiesto: tampoco se
    // cierra. La salida es instalar, y el botón siempre está ahí. Lo levanta
    // `useAppUpdate`, que nunca lo hace con un tramo de recorrido en curso —
    // taparle la pantalla al operario a mitad de captura convertiría una
    // actualización en una pérdida de trabajo.
    if ((currentModal?.data?.blocking as unknown as boolean) === true) return;

    dispatch(closeModal());
    dispatch(
      deselectedRowModalTable(
        currentModal?.data?.namingPersistForm as unknown as string
      )
    );
  }, [dispatch, currentModal]);

  const renderModal = useCallback(() => {
    switch (currentModal.id) {
      case modalIds.DOWNLOAD_HISTORIC:
        return (
          <DownloadHistoricModal
            onClickClose={handleClickClose}
            data={
              currentModal.data as unknown as ModalPropsDownloadHistorical['data']
            }
          />
        );
      case modalIds.DOWNLOAD_MULTIMEDIA_PHOTOS:
        return (
          <DownloadMultimediaPhotosModal
            onClickClose={handleClickClose}
            data={
              currentModal.data as unknown as ModalPropsDownloadMultimediaPhotos['data']
            }
          />
        );
      case modalIds.RELATES_COMMUNICATION_MODAL:
        return (
          <RelatesCommunicationModal
            onClickClose={handleClickClose}
            namingPersistForm={
              currentModal?.data?.namingPersistForm as unknown as string
            }
          />
        );
      case modalIds.CHANGE_CAMPAIGN_TO_OT_MODAL:
        return (
          <ChangeCampaignToOT
            onClickClose={handleClickClose}
            continueClick={() => {
              const handleClickContinue = currentModal?.data
                ?.handleClickContinue as unknown as () => void;

              handleClickContinue();
              handleClickClose();
            }}
          />
        );

      case modalIds.CHANGE_STATE_MODAL:
        return (
          <ChangeStateModal
            indexExpedition={
              currentModal?.data?.indexExpedition as unknown as string
            }
            title={currentModal?.data?.title as unknown as string}
            titleSaveModal={
              currentModal?.data?.titleSaveModal as unknown as string
            }
            onClickClose={handleClickClose}
            continueClick={() => {
              const handleClickContinue = currentModal?.data
                ?.handleClickContinue as unknown as () => void;

              handleClickContinue();
              handleClickClose();
            }}
            revalidatorFunction={
              currentModal?.data?.revalidatorFunction as unknown as () => void
            }
            orderType={currentModal?.data?.orderType as unknown as string}
            currentState={currentModal?.data?.currentState as unknown as string}
            orderId={currentModal?.data?.orderId as unknown as string}
            // RV (2026-07-20): id de la ENTITY SurveillanceRoute — las
            // mutations SR (validate/annul/reopen/finish) lo esperan.
            routeId={currentModal?.data?.routeId as unknown as string}
            roadId={currentModal?.data?.roadId as unknown as string}
            // «Tramos revisados» (D7): solo los pasa la cabecera del RV.
            segmentReviewWarning={
              currentModal?.data?.segmentReviewWarning as unknown as
                | ValidateReviewWarning
                | null
                | undefined
            }
            onReviewRemaining={
              currentModal?.data?.onReviewRemaining as unknown as
                | (() => void)
                | undefined
            }
          />
        );
      case modalIds.ADD_MULTIMEDIA_MODAL:
        return (
          <AddMediaModal
            onClickClose={handleClickClose}
            images={
              currentModal?.data?.images as unknown as ImageData[] | undefined
            }
            onRemoveImage={
              currentModal?.data?.onRemoveImage as unknown as
                | ((id: string) => void)
                | undefined
            }
            onConfirm={
              currentModal?.data?.onConfirm as unknown as
                | (() => void)
                | undefined
            }
            choosePic={() => {
              const handleChoosePick = currentModal?.data
                ?.handleChoosePick as unknown as () => void;

              handleChoosePick();
            }}
            openCamera={() => {
              const handleOpenCamera = currentModal?.data
                ?.handleOpenCamera as unknown as () => void;

              handleClickClose();
              handleOpenCamera();
            }}
          />
        );
      case modalIds.DELETE_PHOTO_MODAL:
        return (
          <DeletePhotoModal
            onClickClose={handleClickClose}
            photoName={currentModal?.data?.photoName as unknown as string}
            onConfirmDelete={
              currentModal?.data
                ?.onConfirmDelete as unknown as () => Promise<void>
            }
          />
        );
      case modalIds.OPEN_CAMERA_MODAL:
        return (
          <OpenCameraModal
            onClickClose={handleClickClose}
            setImagesState={
              currentModal?.data?.setImagesState as unknown as React.Dispatch<
                React.SetStateAction<Img[]>
              >
            }
            dataBaseNamingCaching={currentModal?.data?.dataBaseNamingCaching}
            type={currentModal?.data?.type}
            setFilesState={
              currentModal?.data?.setFilesState as unknown as React.Dispatch<
                React.SetStateAction<CustomFile[]>
              >
            }
            // ⚠️ SuperModal mapea las props EXPLÍCITAMENTE (trampa conocida):
            // sin esta línea, el onImageAdded que ActionEdition pasa por
            // openModal() muere en Redux y la foto de CÁMARA no se registra en
            // DATA_CHANGES (se ve en pantalla pero el save de edición no la sube).
            onImageAdded={
              currentModal?.data?.onImageAdded as unknown as
                | ((image: Img) => void)
                | undefined
            }
          />
        );
      case modalIds.ADD_SKETCH_MODAL:
        return (
          <AddSketchModal
            onClickClose={handleClickClose}
            onAddSketch={
              currentModal?.data?.handleAddSketch as unknown as () => void
            }
          />
        );
      case modalIds.ADD_ACTION_SKETCH_MODAL:
        return (
          <AddActionSketchModal
            onClickClose={handleClickClose}
            onAddSketch={
              currentModal?.data?.handleAddSketch as unknown as () => void
            }
          />
        );
      case modalIds.VIEW_FULLSCREEN_IMAGE:
        return (
          <ViewFullscreenImage
            onClickClose={handleClickClose}
            currentImage={currentModal?.data?.currentImage as unknown as string}
            image={
              currentModal?.data?.image as unknown as {
                publicUrl?: string | null;
                cloudinaryPublicId?: string | null;
                storageProvider?: string | null;
              }
            }
            title={currentModal?.data?.title as unknown as string}
            currentDate={currentModal?.data?.currentDate as unknown as string}
            currentHours={currentModal?.data?.currentHours as unknown as string}
            currentMinutes={
              currentModal?.data?.currentMinutes as unknown as string
            }
            roid={currentModal?.data?.roid as unknown as string}
            publicId={currentModal?.data?.publicId as unknown as string}
          />
        );
      case modalIds.TRANSFER_SUBORDER_MODAL:
        return (
          <TransferSubOrderModal
            onClickClose={handleClickClose}
            namingPersistForm={
              currentModal?.data?.namingPersistForm as unknown as string
            }
            orderToTransfer={
              currentModal?.data?.orderToTransfer as unknown as {
                id: string;
                type: string;
                orderParent: string;
              }
            }
            currentParentOrder={
              currentModal?.data?.currentParentOrder as unknown as {
                id: string;
                type: string;
              }
            }
            revalidatorFunction={
              currentModal?.data?.revalidatorFunction as unknown as () => void
            }
          />
        );
      case modalIds.CONFIRM_TRANSFER_MODAL:
        return (
          <ConfirmTransferModal
            onClickClose={handleClickClose}
            onTransferOrder={
              currentModal.data?.onTransferOrder as unknown as () => void
            }
            orderType={currentModal.data?.orderType as unknown as string}
            newParentOrderType={
              currentModal.data?.newParentOrderType as unknown as string
            }
            isLoading={currentModal.data?.isLoading as unknown as boolean}
            isSameOrder={currentModal.data?.isSameOrder as unknown as boolean}
            // F4 transfer básica→RV (2026-07-21): SuperModal enumera props
            // (trampa conocida) — sin este thread el aviso de desvínculo del
            // tramo nunca llegaría al ConfirmTransferModal.
            showUnlinkSegmentWarning={
              currentModal.data?.showUnlinkSegmentWarning as unknown as boolean
            }
            unlinkWarningRoid={
              currentModal.data?.unlinkWarningRoid as unknown as string
            }
          />
        );
      case modalIds.VERSION_UPDATE_MODAL: {
        const isNewVersion = currentModal.data
          ?.isNewVersion as unknown as boolean;
        const handleContinue = currentModal.data
          ?.onContinue as unknown as () => void;
        const mode =
          (currentModal.data?.mode as unknown as VersionUpdateMode) ?? 'web';

        // Cerrar APLAZA, también en web (desde el 2026-10-01): `useAppUpdate`
        // lo cuenta como cierre y aplica la escalera. Antes en web la X
        // aceptaba, y aceptar se llevaba las colas sin conexión, los borradores
        // y las fotos pendientes (spec 2026-09-30 § 2.3). Con `blocking`, el
        // cierre genérico ya se ignora. Si no se acepta, el aviso vuelve en
        // la siguiente comprobación; un OTA ignorado NO se aplica solo.
        const handleVersionModalClose = () => handleClickClose();

        return (
          <VersionUpdateModal
            onClickClose={handleVersionModalClose}
            version={currentModal.data?.version as unknown as string}
            reason={currentModal.data?.reason as unknown as string}
            isNewVersion={isNewVersion}
            mode={mode}
            apkUrl={currentModal.data?.apkUrl as unknown as string}
            apkChecksum={currentModal.data?.apkChecksum as unknown as string}
            blocking={currentModal.data?.blocking as unknown as boolean}
            // Salta el bloqueo de `handleClickClose` a propósito: el modal solo
            // la ofrece tras un intento de APK fallido.
            onEscape={() => dispatch(closeModal())}
            onContinue={() => {
              handleContinue();
              // En web aplicar = recargar: el aviso se queda en pantalla con el
              // botón ocupado hasta que la página se va. En la app se cierra
              // como siempre (el WebView se reinicia o sale el instalador).
              if (mode !== 'web') handleClickClose();
            }}
          />
        );
      }
      case modalIds.CHANGE_PASSWORD_MODAL:
        return <ChangePasswordModal onClickClose={handleClickClose} />;
      case modalIds.EDIT_TEAM_MODAL:
        return (
          <EditTeamModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as EditTeamModalData}
          />
        );
      case modalIds.EDIT_MATERIAL_TEAMS_MODAL:
        return (
          <EditMaterialTeamsModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as EditMaterialTeamsModalData}
          />
        );
      case modalIds.DELETE_TEAM_MODAL:
        return (
          <DeleteTeamModal
            onClickClose={handleClickClose}
            teamName={currentModal.data?.teamName as unknown as string}
            onConfirmDelete={async () => {
              const handleConfirmDelete = currentModal.data
                ?.onConfirmDelete as unknown as () => void | Promise<void>;
              await handleConfirmDelete();
            }}
            isLoading={currentModal.data?.isLoading as unknown as boolean}
          />
        );
      case modalIds.EDIT_USER_GROUP_MODAL:
        return (
          <EditUserGroupModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as EditUserGroupModalData}
          />
        );
      case modalIds.DELETE_USER_GROUP_MODAL:
        return (
          <DeleteUserGroupModal
            onClickClose={handleClickClose}
            groupName={currentModal.data?.groupName as unknown as string}
            onConfirmDelete={async () => {
              const handleConfirmDelete = currentModal.data
                ?.onConfirmDelete as unknown as () => void | Promise<void>;
              await handleConfirmDelete();
            }}
            isLoading={currentModal.data?.isLoading as unknown as boolean}
          />
        );
      case modalIds.EDIT_EXTERNAL_COMPANY_MODAL:
        return (
          <EditExternalCompanyModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as EditExternalCompanyModalData}
          />
        );
      case modalIds.DELETE_EXTERNAL_COMPANY_MODAL:
        return (
          <DeleteExternalCompanyModal
            onClickClose={handleClickClose}
            companyName={currentModal.data?.companyName as unknown as string}
            onConfirmDelete={async () => {
              const handleConfirmDelete = currentModal.data
                ?.onConfirmDelete as unknown as () => void | Promise<void>;
              await handleConfirmDelete();
            }}
          />
        );
      case modalIds.EDIT_ROUTE_PRESET_MODAL:
        return (
          <RoutePresetModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as RoutePresetModalData}
          />
        );
      case modalIds.DELETE_ROUTE_PRESET_MODAL:
        return (
          <DeleteRoutePresetModal
            onClickClose={handleClickClose}
            presetName={currentModal.data?.presetName as unknown as string}
            onConfirmDelete={async () => {
              const handleConfirmDelete = currentModal.data
                ?.onConfirmDelete as unknown as () => void | Promise<void>;
              await handleConfirmDelete();
            }}
          />
        );
      case modalIds.VALIDATION_ERRORS_MODAL:
        return (
          <ValidationErrorsModal
            onClickClose={handleClickClose}
            tabsWithErrors={
              currentModal.data
                ?.tabsWithErrors as unknown as TabValidationStatus[]
            }
            validationSummary={
              currentModal.data?.validationSummary as unknown as string
            }
            onNavigateToTab={(tabKey: string) => {
              const handleNavigateToTab = currentModal.data
                ?.onNavigateToTab as unknown as (tabKey: string) => void;
              handleNavigateToTab(tabKey);
            }}
          />
        );
      case modalIds.CONTINUE_SHIFT:
        return (
          <ContinueShiftModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as ContinueShiftModalData}
          />
        );
      case modalIds.PAUSE_SURVEILLANCE_ROUTE:
        return (
          <PauseSurveillanceRouteModal
            onClickClose={handleClickClose}
            data={
              currentModal.data as unknown as PauseSurveillanceRouteModalData
            }
          />
        );
      case modalIds.NEW_SURVEILLANCE_SEGMENT:
        return (
          <NewSurveillanceSegmentModal
            onClickClose={handleClickClose}
            data={
              currentModal.data as unknown as NewSurveillanceSegmentModalData
            }
          />
        );
      case modalIds.NEW_DASHBOARD_WIDGET:
        return (
          <NewWidgetModal
            onClickClose={handleClickClose}
            availableWidgets={
              currentModal.data
                ?.availableWidgets as unknown as DashboardWidgetCatalogEntry[]
            }
            onAddWidget={
              currentModal.data?.onAddWidget as unknown as (id: string) => void
            }
          />
        );
      case modalIds.CONFIRM_CLOSE_ORDER_MODAL:
        return (
          <ConfirmCloseOrderModal
            onClickClose={handleClickClose}
            onConfirmClose={async () => {
              const handleConfirm = currentModal.data
                ?.onConfirmClose as unknown as () => void | Promise<void>;
              await handleConfirm();
            }}
            onCancelClose={() => {
              const customCancelHandler = currentModal.data
                ?.onCancelClose as unknown as (() => void) | undefined;
              if (customCancelHandler) {
                customCancelHandler();
              } else {
                handleClickClose();
              }
            }}
            onSetPendingAT={
              currentModal.data?.onSetPendingAT
                ? async () => {
                    const handlePendingAT = currentModal.data
                      ?.onSetPendingAT as unknown as () => void | Promise<void>;
                    await handlePendingAT();
                  }
                : undefined
            }
            onConfirmCloseWithActions={
              currentModal.data?.onConfirmCloseWithActions
                ? async () => {
                    const handler = currentModal.data
                      ?.onConfirmCloseWithActions as unknown as () => void | Promise<void>;
                    await handler();
                  }
                : undefined
            }
            isLoading={currentModal.data?.isLoading as unknown as boolean}
            parentOrderId={
              currentModal.data?.parentOrderId as unknown as string
            }
            parentRoid={currentModal.data?.parentRoid as unknown as string}
            orderType={currentModal.data?.orderType as unknown as string}
            routeId={currentModal.data?.routeId as unknown as string}
            orderDescription={
              currentModal.data?.orderDescription as unknown as string
            }
            orderState={currentModal.data?.orderState as unknown as string}
          />
        );
      case modalIds.GPS_REQUIRED:
        return (
          <GpsRequiredModal
            data={currentModal.data as unknown as GpsRequiredModalData}
          />
        );
      case modalIds.BACKGROUND_LOCATION_REQUIRED:
        return (
          <BackgroundLocationRequiredModal
            data={
              currentModal.data as unknown as BackgroundLocationRequiredModalData
            }
          />
        );
      case modalIds.PHOTO_PERMISSIONS_REQUIRED:
        return (
          <PhotoPermissionsRequiredModal
            data={
              currentModal.data as unknown as PhotoPermissionsRequiredModalData
            }
          />
        );
      case modalIds.CHANGE_DIRECTION:
        return (
          <ChangeDirectionModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as ChangeDirectionModalData}
          />
        );
      case modalIds.RELATE_SEGMENT:
        return (
          <RelateSegmentModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as RelateSegmentModalData}
          />
        );
      // Cross-link 3 (2026-09-22): recorrido ↔ OT en las dos caras.
      case modalIds.RELATE_ORDER_TO_ROUTE:
        return (
          <RelateOrderToRouteModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as RelateOrderToRouteModalData}
          />
        );
      case modalIds.CHANGE_PAUSE_CAUSE:
        return (
          <ChangePauseCauseModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as ChangePauseCauseModalData}
          />
        );
      case modalIds.FORCE_CLOSE_ROUTE:
        return (
          <ForceCloseRouteModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as ForceCloseRouteModalData}
          />
        );
      case modalIds.EDIT_SEGMENT:
        return (
          <EditSegmentModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as EditSegmentModalData}
          />
        );
      // «Registrar parada» (spec 2026-10-06). Todo el payload viaja en `data`.
      case modalIds.REGISTER_SEGMENT_STOP:
        return (
          <RegisterStopModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as RegisterStopModalData}
          />
        );
      case modalIds.UNDO_SEGMENT_STOP:
        return (
          <UndoSegmentStopModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as UndoSegmentStopModalData}
          />
        );
      // «Tramos revisados» (spec 2026-10-27): confirmar «desmarcar todos».
      case modalIds.UNREVIEW_ALL_SEGMENTS:
        return (
          <UnreviewAllSegmentsModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as UnreviewAllSegmentsModalData}
          />
        );
      // «Transferir tramo» (spec 2026-10-07).
      case modalIds.TRANSFER_ROUTE_SEGMENT:
        return (
          <TransferSegmentModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as TransferSegmentModalData}
          />
        );
      // «Mover tramos» (plan 2026-10-15 § 3).
      case modalIds.MOVE_ROUTE_SEGMENTS:
        return (
          <MoveSegmentsModal
            onClickClose={handleClickClose}
            data={currentModal.data as unknown as MoveSegmentsModalData}
          />
        );
      default:
        return null;
    }
  }, [currentModal]);

  return (
    <BaseModal handleClickClose={handleClickClose}>
      {/* Un modal que revienta se cierra y avisa; no tumba la aplicación. */}
      <ModalErrorBoundary>{renderModal()}</ModalErrorBoundary>
    </BaseModal>
  );
}
